// TimelineWeekView.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
    buildTopHeaderSegments,
    DAY_MS,
    formatTimelineRangeText,
    getInclusiveEndOffsetMs,
    getScheduledBounds,
    getScheduledRows,
    getTimelineBarStyle,
    resolveViewportTimelineBounds,
} from '../shared/timelineViewUtils';
import TimelineDependencyOverlay from 'views/timeline/components/TimelineDependencyOverlay';
import useTimelineScheduleEdit from 'views/timeline/hooks/useTimelineScheduleEdit';
import Confirm from 'components/confirm/Confirm';

// CSS
import './TimelineWeekView.css';

const WEEK_MS = 7 * DAY_MS;
const WEEK_COLUMN_WIDTH = 88;
const WEEK_ROW_HEIGHT = 36;

const startOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const endOfDay = (date) => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next;
};

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const addMonths = (date, months) => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next;
};

const startOfMonth = (date) => {
    const next = startOfDay(date);
    next.setDate(1);
    return next;
};

const endOfMonth = (date) => {
    const firstOfNextMonth = startOfMonth(addDays(date, 32));
    return endOfDay(addDays(firstOfNextMonth, -1));
};

const firstWeekStartInMonth = (date) => {
    const firstDay = startOfMonth(date);
    const dayOffset = (firstDay.getDay() + 6) % 7;
    const daysUntilMonday = (7 - dayOffset) % 7;
    return startOfDay(addDays(firstDay, daysUntilMonday));
};

const lastWeekStartInMonth = (date) => {
    const lastDay = endOfMonth(date);
    const dayOffset = (lastDay.getDay() + 6) % 7;
    return startOfDay(addDays(lastDay, -dayOffset));
};

const startOfWeekMonday = (date) => {
    const next = startOfDay(date);
    const diff = (next.getDay() + 6) % 7;
    next.setDate(next.getDate() - diff);
    return next;
};

/**
 * TimelineWeekView
 *
 * Right-side timeline renderer for week-scale timeline.
 *
 * @returns {ReactNode} - TimelineWeekView.
 */
const TimelineWeekView = React.memo(({
    timelineRows = [],
    timelineFieldDef,
    typeDef = null,
    viewportBounds = null,
    onNodeClick,
    onNodeDoubleClick,
    showDependencies = false,
    allowScheduleEdit = false,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [loadedTrailingMonths, setLoadedTrailingMonths] = useState(0);
    // ----- REFS ----- //

    const scrollRef = useRef(null);
    const lastLoadTriggerWidthRef = useRef(0);

    const scheduledRows = useMemo(() => getScheduledRows(timelineRows), [timelineRows]);

    const scheduleEditEntries = useMemo(
      () => scheduledRows.map((row) => row.entry).filter(Boolean),
      [scheduledRows],
    );

    const liveRangesByKey = useMemo(() => {
        const map = {};
        scheduledRows.forEach((row) => {
            const key = row?.entry?.key;
            const range = row?.entry?.range;
            if (!key || !range?.start || !range?.end) return;
            map[key] = range;
        });
        return map;
    }, [scheduledRows]);

    const {
        beginDrag,
        applyPreviewRange,
        consumeClickSuppression,
        dragPreviewKey,
        cascadeConfirmOpen,
        cascadeConfirmMessage,
        cascadeConfirmBusy,
        handleCascadeConfirm,
        handleCascadePin,
        setCascadeConfirmOpen,
    } = useTimelineScheduleEdit({
        typeDef,
        timelineFieldDef,
        enabled: allowScheduleEdit,
        pxPerDay: WEEK_COLUMN_WIDTH / 7,
        liveRangesByKey,
        entries: scheduleEditEntries,
    });

    const scheduledSpanResetToken = useMemo(() => {
        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return `none_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
        return `${scheduledBounds.minStart.getTime()}_${scheduledBounds.maxEnd.getTime()}_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
    }, [scheduledRows, timelineFieldDef?.key, timelineFieldDef?.name]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setLoadedTrailingMonths(0);
        lastLoadTriggerWidthRef.current = 0;
    }, [scheduledSpanResetToken]);

    // ----- MEMOS ----- //

    const bounds = useMemo(() => {
        const viewport = resolveViewportTimelineBounds(viewportBounds);
        if (viewport) return viewport;

        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return null;

        const startMonth = startOfMonth(scheduledBounds.minStart);
        const dataEndMonth = startOfMonth(scheduledBounds.maxEnd);
        const minTwoMonthEnd = startOfMonth(addMonths(startMonth, 1));
        const baseEndMonth = dataEndMonth.getTime() < minTwoMonthEnd.getTime()
            ? minTwoMonthEnd
            : dataEndMonth;
        const endMonth = loadedTrailingMonths > 0
            ? startOfMonth(addMonths(baseEndMonth, loadedTrailingMonths))
            : baseEndMonth;

        const start = firstWeekStartInMonth(startMonth);
        const end = endOfDay(addDays(lastWeekStartInMonth(endMonth), 6));
        return { start, end };
    }, [scheduledRows, loadedTrailingMonths, viewportBounds]);

    // ----- HANDLERS ----- //

    const handleScroll = useCallback(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const nearRightEdge = scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 48;
        if (!nearRightEdge) return;

        if (lastLoadTriggerWidthRef.current === scrollEl.scrollWidth) return;
        lastLoadTriggerWidthRef.current = scrollEl.scrollWidth;
        setLoadedTrailingMonths((prev) => prev + 1);
    }, []);

    // ----- EFFECTS ----- //

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const onScroll = () => handleScroll();
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, [handleScroll]);

    // ----- MEMOS ----- //

    const columns = useMemo(() => {
        if (!bounds) return [];

        const out = [];
        let cursor = startOfWeekMonday(bounds.start);

        while (cursor.getTime() <= bounds.end.getTime()) {
            const weekStart = new Date(cursor);
            const next = addDays(cursor, 7);
            const weekEnd = new Date(next.getTime() - 1);
            out.push({
                key: `w_${weekStart.getTime()}`,
                start: weekStart,
                end: weekEnd,
                topLabel: weekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                bottomLabel: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            });
            cursor = next;
        }

        return out;
    }, [bounds]);

    const topHeaderSegments = useMemo(
        () => buildTopHeaderSegments(columns, { labelKey: 'topLabel', keyPrefix: 'm' }),
        [columns]
    );

    const trackWidth = useMemo(() => (
        Math.max(720, (columns.length || 1) * WEEK_COLUMN_WIDTH)
    ), [columns.length]);

    const spanMs = useMemo(() => {
        if (!bounds) return 0;
        return Math.max(1, bounds.end.getTime() - bounds.start.getTime());
    }, [bounds]);

    const dependencyBarItems = useMemo(() => {
        if (!showDependencies || !bounds || !spanMs) return [];
        const inclusiveEndMs = getInclusiveEndOffsetMs(timelineFieldDef?.type);
        const items = [];

        (Array.isArray(timelineRows) ? timelineRows : []).forEach((row, rowIndex) => {
            if (row?.type !== 'item') return;
            const previewRange = applyPreviewRange(row.entry);
            const entryForBar = previewRange
                ? { ...row.entry, range: previewRange }
                : row.entry;
            const bar = getTimelineBarStyle({
                entry: entryForBar,
                bounds,
                spanMs,
                trackWidth,
                minDurationMs: DAY_MS,
                minWidthPercent: Math.max((WEEK_MS / spanMs) * 100 * 0.3, 1.1),
                inclusiveEndMs,
            });
            if (bar && entryForBar?.key) {
                items.push({
                    key: entryForBar.key,
                    entry: entryForBar,
                    bar,
                    rowCenterY: (rowIndex * WEEK_ROW_HEIGHT) + (WEEK_ROW_HEIGHT / 2),
                });
            }
        });

        return items;
    }, [
        showDependencies,
        bounds,
        spanMs,
        timelineRows,
        trackWidth,
        timelineFieldDef?.type,
        applyPreviewRange,
    ]);

    if (!bounds || columns.length === 0) {

        return (
            <div className="timeline-week-view-container">

                {/* EMPTY */}
                <div
                  className="timeline-week-view-empty"
                >
                    No scheduled timeline items yet.
                </div>
            </div>
        );
    }
    // ----- RENDER ----- //

    return (
        <>
        <div
          className="timeline-week-view-container"
          style={{
                '--timeline-week-column-width': `${WEEK_COLUMN_WIDTH}px`,
            }}
        >

            {/* SCROLL */}
            <div className="timeline-week-view-scroll" ref={scrollRef}>

                {/* TIMELINE WEEK HEADER */}
                <div
                  className="timeline-week-header"
                  style={{
                        borderBottom: `1px solid ${theme.status}`,
                        width: `${trackWidth}px`,
                    }}
                >

                    {/* TIMELINE WEEK HEADER ROW TOP */}
                    <div
                      className="timeline-week-header-row timeline-week-header-row-top"
                      style={{
                        borderBottom: `1px solid ${theme.status}`,
                      }}
                    >
                        {topHeaderSegments.map((segment) => (
                            <div
                              className="timeline-week-header-cell timeline-week-header-cell-top"
                              style={{
                                width: `${segment.span * WEEK_COLUMN_WIDTH}px`,
                              }}
                              key={segment.key}
                              title={segment.label}
                            >
                                {segment.label}
                            </div>
                        ))}
                    </div>

                    {/* TIMELINE WEEK HEADER ROW BOTTOM */}
                    <div
                      className="timeline-week-header-row timeline-week-header-row-bottom"
                      style={{
                            backgroundImage: `repeating-linear-gradient(to right, ${theme.status} 0, ${theme.status} 1px, transparent 1px, transparent ${WEEK_COLUMN_WIDTH}px)`,
                        }}
                    >
                        {columns.map((column) => (
                            <div
                              className="timeline-week-header-cell timeline-week-header-cell-bottom"
                              style={{
                                width: `${WEEK_COLUMN_WIDTH}px`,
                              }}
                              key={column.key}
                              title={column.bottomLabel}
                            >
                                {column.bottomLabel}
                            </div>
                        ))}
                    </div>
                </div>

                {/* TIMELINE WEEK BODY */}
                <div
                  className="timeline-week-body"
                  style={{
                    width: `${trackWidth}px`,
                    position: 'relative',
                  }}
                >
                    <TimelineDependencyOverlay
                      visible={showDependencies}
                      barItems={dependencyBarItems}
                      stroke={theme.foregroundMuted || theme.foreground || '#888'}
                    />
                    {timelineRows.map((row, rowIndex) => {
                        const rowDividerStyle = rowIndex > 0
                            ? { borderTop: `1px solid ${theme.status}` }
                            : undefined;

                        if (row.type === 'group-header') {
                            return (
                                <div
                                  className="timeline-week-row timeline-week-row-group"
                                  style={{
                                    ...rowDividerStyle,
                                  }}
                                  key={row.key}
                                />
                            );
                        }

                        const entry = row.entry;
                        const isUnscheduled = row.type === 'item-unscheduled';
                        const previewRange = applyPreviewRange(entry);
                        const entryForBar = previewRange
                            ? { ...entry, range: previewRange }
                            : entry;
                        const barStyle = getTimelineBarStyle({
                            entry: entryForBar,
                            bounds,
                            spanMs,
                            trackWidth,
                            minDurationMs: DAY_MS,
                            minWidthPercent: Math.max((WEEK_MS / spanMs) * 100 * 0.3, 1.1),
                            inclusiveEndMs: getInclusiveEndOffsetMs(timelineFieldDef?.type),
                        });
                        const rangeText = formatTimelineRangeText(entryForBar?.range, timelineFieldDef?.type);
                        const isDraggingBar = Boolean(dragPreviewKey && dragPreviewKey === entry?.key);
                        const showBarLabel = isDraggingBar || barStyle?.widthPx >= 120;

                        return (
                            <div
                              className="timeline-week-row timeline-week-row-item"
                              style={{
                                    ...rowDividerStyle,
                                    backgroundImage: `repeating-linear-gradient(to right, ${theme.status} 0, ${theme.status} 1px, transparent 1px, transparent ${WEEK_COLUMN_WIDTH}px)`,
                                }}
                              key={row.key}
                              onClick={(event) => {
                                if (consumeClickSuppression()) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  return;
                                }
                                onNodeClick?.(event, entry?.node);
                              }}
                              onDoubleClick={(event) => onNodeDoubleClick?.(event, entry?.node)}
                            >
                                {!isUnscheduled && barStyle && (
                                    <div
                                      className={`timeline-week-bar${entryForBar?.range?.isMilestone ? ' is-milestone' : ''}${allowScheduleEdit ? ' is-editable' : ''}${isDraggingBar ? ' is-dragging' : ''}`}
                                      style={{
                                            backgroundColor: entry?.barColor || theme.highlight,
                                            color: entry?.barForeground || undefined,
                                            left: barStyle.left,
                                            width: barStyle.width,
                                            cursor: allowScheduleEdit ? (isDraggingBar ? 'grabbing' : 'grab') : undefined,
                                        }}
                                      title={rangeText}
                                      onClick={allowScheduleEdit
                                        ? (event) => {
                                          // Editable bars own click vs drag; don't bubble to the row.
                                          event.stopPropagation();
                                          if (consumeClickSuppression()) {
                                            event.preventDefault();
                                            return;
                                          }
                                          onNodeClick?.(event, entry?.node);
                                        }
                                        : undefined}
                                      onPointerDown={allowScheduleEdit
                                        ? (event) => beginDrag(
                                          event,
                                          { ...entry, range: entryForBar.range },
                                          'move',
                                          {
                                            onPressClick: () => onNodeClick?.(event, entry?.node),
                                          },
                                        )
                                        : undefined}
                                    >
                                        {allowScheduleEdit ? (
                                            <>
                                              <span
                                                className="timeline-week-bar-handle is-start"
                                                onPointerDown={(event) => beginDrag(event, { ...entry, range: entryForBar.range }, 'resize-start')}
                                              />
                                              <span
                                                className="timeline-week-bar-handle is-end"
                                                onPointerDown={(event) => beginDrag(event, { ...entry, range: entryForBar.range }, 'resize-end')}
                                              />
                                            </>
                                        ) : null}

                                        {isDraggingBar && rangeText ? (
                                            <span className="timeline-week-bar-drag-chip">
                                              {rangeText}
                                            </span>
                                        ) : null}

                                        {/* TIMELINE WEEK BAR LABEL */}
                                        {!isDraggingBar && showBarLabel && (
                                            <span
                                              className="timeline-week-bar-label"
                                            >{rangeText}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>

        <Confirm
          title="Shift dependent tasks?"
          message={cascadeConfirmMessage}
          isOpen={cascadeConfirmOpen}
          setOpen={setCascadeConfirmOpen}
          confirmLabel={cascadeConfirmBusy ? 'Saving…' : 'Shift Dependents'}
          secondaryLabel="Only This Task"
          onConfirm={handleCascadeConfirm}
          onSecondary={handleCascadePin}
        />
        </>
    );
});

// Dev Tools
TimelineWeekView.displayName = 'TimelineWeekView';

export default TimelineWeekView;
